import { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAX_RETRIES = 1;
const UPLOAD_TIMEOUT = 180000; // 3分钟：含浏览器上传+服务器转存到云存储

// 图片压缩：最大宽高 2048px，质量 0.8，可减少 60-90% 体积
const compressImage = (file, maxDim = 2048, quality = 0.8) => {
  return new Promise((resolve) => {
    // 非图片或 GIF/SVG 不压缩
    if (!file.type.startsWith('image/') || file.type === 'image/gif' || file.type === 'image/svg+xml') {
      return resolve(file);
    }
    // 小于 500KB 不压缩
    if (file.size < 500 * 1024) {
      return resolve(file);
    }

    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;

      // 等比缩放
      if (width > maxDim || height > maxDim) {
        if (width > height) {
          height = Math.round(height * (maxDim / width));
          width = maxDim;
        } else {
          width = Math.round(width * (maxDim / height));
          height = maxDim;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob || blob.size >= file.size) {
            // 压缩后反而更大，用原文件
            return resolve(file);
          }
          const ext = file.name.split('.').pop().toLowerCase();
          const name = file.name.replace(`.${ext}`, '') + '_compressed.jpg';
          resolve(new File([blob], name, { type: 'image/jpeg' }));
        },
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file); // 压缩失败用原文件
    };
    img.src = url;
  });
};

export const FileUploader = ({ onUpload, accept, type = "image", label }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('');
  const abortRef = useRef(null);
  const uploadDoneRef = useRef(false); // 追踪浏览器是否已发完数据
  const token = localStorage.getItem('token');

  const uploadWithRetry = async (formData, retryCount = 0) => {
    const controller = new AbortController();
    abortRef.current = controller;
    uploadDoneRef.current = false;

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const endpoint = type === 'video' ? `${API}/upload/video` : `${API}/upload/image`;
      const res = await axios.post(endpoint, formData, {
        headers,
        signal: controller.signal,
        timeout: UPLOAD_TIMEOUT,
        onUploadProgress: (e) => {
          if (e.total) {
            const pct = Math.round((e.loaded / e.total) * 100);
            setProgress(pct);
            if (pct >= 100) {
              uploadDoneRef.current = true;
              setStatus('服务器存储中，请耐心等待...');
            }
          }
        },
      });
      return res;
    } catch (err) {
      if (axios.isCancel(err)) throw err;
      // 数据已全部发送到服务器（100%）→ 不重试，等服务器处理
      // 数据未发完 → 可以重试
      if (!uploadDoneRef.current && retryCount < MAX_RETRIES) {
        setStatus(`重试 ${retryCount + 1}/${MAX_RETRIES}...`);
        setProgress(0);
        await new Promise(r => setTimeout(r, 1500));
        return uploadWithRetry(formData, retryCount + 1);
      }
      throw err;
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setProgress(0);

    try {
      let fileToUpload = file;

      // 图片自动压缩
      if (type === 'image' && file.type.startsWith('image/')) {
        setStatus('压缩中...');
        const originalSize = file.size;
        fileToUpload = await compressImage(file);
        const ratio = Math.round((1 - fileToUpload.size / originalSize) * 100);
        if (ratio > 5) {
          setStatus(`已压缩 ${ratio}%`);
        }
      }

      setStatus('上传中...');
      const formData = new FormData();
      formData.append('file', fileToUpload);

      const res = await uploadWithRetry(formData);
      onUpload(res.data.url);
      toast.success('上传成功');
    } catch (err) {
      if (axios.isCancel(err)) {
        toast.info('上传已取消');
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        toast.error('上传超时，请检查网络后重试');
      } else {
        const detail = err.response?.data?.detail;
        const msg = typeof detail === 'string' ? detail : '上传失败，请重试';
        toast.error(msg);
      }
    } finally {
      setUploading(false);
      setProgress(0);
      setStatus('');
      abortRef.current = null;
      e.target.value = '';
    }
  };

  const handleCancel = () => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
  };

  const displayText = uploading
    ? (progress >= 100
        ? (status || '服务器存储中...')
        : progress > 0
          ? `上传中 ${progress}%`
          : (status || '准备中...'))
    : (label || '上传文件 / Upload File');

  return (
    <div className="inline-flex items-center gap-2">
      <Button variant="outline" size="sm" disabled={uploading} asChild>
        <label className="cursor-pointer">
          <input type="file" accept={accept} onChange={handleFileChange} className="hidden" />
          {displayText}
        </label>
      </Button>
      {uploading && (
        <Button variant="ghost" size="sm" onClick={handleCancel} className="text-red-500 h-7 px-2">
          取消
        </Button>
      )}
    </div>
  );
};

export default FileUploader;
